import React, { useState, useEffect, useRef } from 'react';
import { User, Lock, Save, AlertCircle, CheckCircle, Eye, EyeOff, Camera, X } from 'lucide-react';
import { api } from '../../api/client';
import { useProfile } from '../../contexts/ProfileContext';
import ProfileImage from '../../components/shared/ProfileImage';
import PasswordValidation, { isPasswordValid } from '../../components/shared/PasswordValidation';

export default function Profile() {
    const [loading, setLoading] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const fileInputRef = useRef(null);

    // Use profile context
    const { profile: contextProfile, updateProfileImage, updateProfile, fetchProfile: refreshProfile } = useProfile();

    const [profile, setProfile] = useState({
        full_name: '',
        email: '',
        profile_image_url: null
    });
    const [passwordData, setPasswordData] = useState({
        new_password: '',
        confirm_password: ''
    });

    useEffect(() => {
        fetchProfile();
    }, []);

    // Sync with context profile
    useEffect(() => {
        if (contextProfile) {
            setProfile({
                full_name: contextProfile.full_name || '',
                email: contextProfile.email,
                profile_image_url: contextProfile.profile_image_url || null
            });
        }
    }, [contextProfile]);

    // Auto-dismiss success messages after 3 seconds
    useEffect(() => {
        if (message.type === 'success') {
            const timer = setTimeout(() => {
                setMessage({ type: '', text: '' });
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [message]);

    const fetchProfile = async () => {
        try {
            const response = await api.get('/api/user/profile');
            setProfile({
                full_name: response.data.full_name || '',
                email: response.data.email,
                profile_image_url: response.data.profile_image_url || null
            });
        } catch (error) {
            console.error('Failed to fetch profile:', error);
            setMessage({ type: 'error', text: 'Failed to load profile' });
        }
    };

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfile(prev => ({ ...prev, [name]: value }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setMessage({ type: 'error', text: 'Please select an image file' });
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            setMessage({ type: 'error', text: 'Image size must be less than 5MB' });
            return;
        }

        setUploadingImage(true);
        setMessage({ type: '', text: '' });

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await api.post('/api/upload/profile-image', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Get the pre-signed URL from response
            const newImageUrl = response.data.profile_image_url;

            // Update local state immediately
            setProfile(prev => ({
                ...prev,
                profile_image_url: newImageUrl
            }));

            // CRITICAL: Update context immediately to refresh sidebar
            // This will trigger re-render of all components using the profile context
            await updateProfileImage(newImageUrl);

            setMessage({ type: 'success', text: 'Profile image uploaded successfully!' });

            // Optional: Refresh full profile data in background
            // This ensures all profile data is in sync without blocking UI
            setTimeout(async () => {
                await refreshProfile();
            }, 500);

        } catch (error) {
            console.error('Failed to upload image:', error);
            setMessage({
                type: 'error',
                text: error.response?.data?.detail || 'Failed to upload image'
            });
        } finally {
            setUploadingImage(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleRemoveImage = async () => {
        if (!window.confirm('Are you sure you want to remove your profile image?')) {
            return;
        }

        setUploadingImage(true);
        setMessage({ type: '', text: '' });

        try {
            // Use DELETE endpoint for proper image removal
            await api.delete('/api/upload/profile-image');

            setProfile(prev => ({ ...prev, profile_image_url: null }));

            // Update context
            updateProfileImage(null);

            setMessage({ type: 'success', text: 'Profile image removed successfully!' });
        } catch (error) {
            console.error('Failed to remove image:', error);
            setMessage({
                type: 'error',
                text: error.response?.data?.detail || 'Failed to remove profile image'
            });
        } finally {
            setUploadingImage(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        // Validate passwords if attempting to change
        if (passwordData.new_password || passwordData.confirm_password) {
            if (passwordData.new_password !== passwordData.confirm_password) {
                setMessage({ type: 'error', text: 'Passwords do not match' });
                setLoading(false);
                return;
            }
            if (!isPasswordValid(passwordData.new_password)) {
                setMessage({ type: 'error', text: 'Please ensure your password meets all requirements' });
                setLoading(false);
                return;
            }
        }

        try {
            const updateData = {
                full_name: profile.full_name
            };

            // Only include password if it's being changed
            if (passwordData.new_password) {
                updateData.new_password = passwordData.new_password;
            }

            await api.put('/api/user/profile', updateData);

            // Update context with new profile data
            updateProfile({ full_name: profile.full_name });

            setMessage({ type: 'success', text: 'Profile updated successfully!' });

            // Clear password fields
            setPasswordData({
                new_password: '',
                confirm_password: ''
            });

            // Refresh profile data
            await fetchProfile();

            // Refresh context to sync with sidebar
            await refreshProfile();
        } catch (error) {
            console.error('Failed to update profile:', error);
            setMessage({
                type: 'error',
                text: error.response?.data?.detail || 'Failed to update profile. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-3 sm:p-6">
            <div className="mb-6 sm:mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2 sm:gap-3">
                    <User className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                    My Profile
                </h1>
                <p className="text-gray-600 mt-2 text-sm sm:text-base">Manage your account settings and password</p>
            </div>

            {message.text && (
                <div className={`mb-4 sm:mb-6 p-3 sm:p-4 rounded-lg flex items-center gap-2 sm:gap-3 ${message.type === 'success'
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
                    }`}>
                    {message.type === 'success' ? (
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    )}
                    <span className={`text-sm sm:text-base ${message.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                        {message.text}
                    </span>
                </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-4 sm:p-8 space-y-6 sm:space-y-8">
                {/* Profile Information Section */}
                <div>
                    <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6 flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Profile Information
                    </h2>

                    <div className="space-y-4">
                        {/* Profile Image Upload */}
                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 pb-6 border-b">
                            <div className="relative">
                                <ProfileImage
                                    imageUrl={profile.profile_image_url}
                                    alt="Profile"
                                    size="2xl"
                                    cacheBust={true}
                                />
                                {uploadingImage && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 text-center sm:text-left">
                                <h3 className="font-medium text-gray-900 mb-2">Profile Photo</h3>
                                <p className="text-xs sm:text-sm text-gray-600 mb-3">
                                    Upload a profile picture. Max size 5MB.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleImageUpload}
                                        accept="image/*"
                                        className="hidden"
                                        disabled={uploadingImage}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploadingImage}
                                        className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
                                    >
                                        <Camera className="w-4 h-4" />
                                        {profile.profile_image_url ? 'Change Photo' : 'Upload Photo'}
                                    </button>
                                    {profile.profile_image_url && (
                                        <button
                                            type="button"
                                            onClick={handleRemoveImage}
                                            disabled={uploadingImage}
                                            className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
                                        >
                                            <X className="w-4 h-4" />
                                            Remove
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="full_name" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                Full Name
                            </label>
                            <input
                                type="text"
                                id="full_name"
                                name="full_name"
                                value={profile.full_name}
                                onChange={handleProfileChange}
                                className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                                placeholder="Enter your full name"
                                maxLength={30}
                                required
                            />
                            <p className="text-xs text-gray-500 mt-1">{profile.full_name.length}/30 characters</p>
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                value={profile.email}
                                className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed text-sm sm:text-base"
                                disabled
                            />
                            <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                        </div>
                    </div>
                </div>

                {/* Password Change Section */}
                <div className="border-t pt-6 sm:pt-8">
                    <h2 className="text-lg sm:text-xl font-semibold mb-2 flex items-center gap-2 text-gray-900 bg-white">
                        <Lock className="w-5 h-5" />
                        Change Password
                    </h2>
                    <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
                        Leave blank to keep your current password
                    </p>

                    <div className="space-y-4">
                        {/* New password */}
                        <div>
                            <label
                                htmlFor="new_password"
                                className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2"
                            >
                                New Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showNewPassword ? "text" : "password"}
                                    id="new_password"
                                    name="new_password"
                                    value={passwordData.new_password}
                                    onChange={handlePasswordChange}
                                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 text-gray-900 bg-white text-sm sm:text-base"
                                    placeholder="8-12 characters"
                                    maxLength={12}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 z-10"
                                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                                >
                                    {showNewPassword ? (
                                        <Eye className="w-5 h-5" />
                                    ) : (
                                        <EyeOff className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                            <PasswordValidation password={passwordData.new_password} />
                        </div>

                        {/* Confirm new password */}
                        <div>
                            <label
                                htmlFor="confirm_password"
                                className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2"
                            >
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    id="confirm_password"
                                    name="confirm_password"
                                    value={passwordData.confirm_password}
                                    onChange={handlePasswordChange}
                                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 text-gray-900 bg-white text-sm sm:text-base"
                                    placeholder="Confirm new password"
                                    maxLength={12}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 z-10"
                                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                >
                                    {showConfirmPassword ? (
                                        <Eye className="w-5 h-5" />
                                    ) : (
                                        <EyeOff className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {passwordData.new_password && passwordData.confirm_password &&
                            passwordData.new_password !== passwordData.confirm_password && (
                                <p className="text-xs sm:text-sm text-red-600 flex items-center gap-1">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    Passwords do not match
                                </p>
                            )}
                    </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-center sm:justify-end pt-4 sm:pt-6 border-t">
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto justify-center text-sm sm:text-base"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                Updating...
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5" />
                                Update Profile
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Security Tips */}
            <div className="mt-4 sm:mt-6 bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                <h3 className="font-semibold text-blue-900 mb-2 text-sm sm:text-base">Security Tips</h3>
                <ul className="text-xs sm:text-sm text-blue-800 space-y-1 list-disc list-inside">
                    <li>Use a strong password with 8-12 characters</li>
                    <li>Include uppercase letters, numbers, and special characters</li>
                    <li>Don't reuse passwords from other websites</li>
                    <li>Change your password regularly for better security</li>
                </ul>
            </div>
        </div>
    );
}